import { Container, Graphics, Sprite, type Texture, FillGradient } from 'pixi.js';
import { BACKGROUND_TYPE, COLOR_TYPE } from '@/constants';
import type { BackgroundContent, ColorContent } from '@/types/canvas-type';
import { textureManager } from '@/managers/TextureManager';

/**
 * 背景素材类
 *
 * 根据 BackgroundContent 类型渲染不同背景：
 * - color: 纯色 / 线性渐变 / 径向渐变（使用 Pixi Graphics）
 * - image: 加载网络/本地图片（使用 Sprite）
 * - video: 占位黑色背景（后续可扩展视频纹理）
 *
 * 继承自 Container，可直接加入场景图。
 * 由 BackgroundElement（继承 Element）作为内部渲染器使用。
 */
export class BackgroundAsset extends Container {
  /** 默认背景数据：白色纯色 */
  private _data: BackgroundContent = {
    type: BACKGROUND_TYPE.COLOR,
    content: { type: COLOR_TYPE.PURE, color: '#ffffff' }
  };

  private _graphics: Graphics | null = null;
  private _sprite: Sprite | null = null;
  private _loadedUrl: string | null = null;

  private _width = 1920;
  private _height = 1080;

  constructor(width?: number, height?: number) {
    super();
    this.label = 'background-asset';

    if (width !== undefined) this._width = width;
    if (height !== undefined) this._height = height;

    // 构造函数中立即渲染默认白色背景（纯色渲染是同步的）
    this._renderColor(this._data.content as ColorContent);
  }

  // ============================================================
  // 公共生命周期
  // ============================================================

  /**
   * 加载背景资源并渲染
   * @param data  背景数据
   * @param width  画布宽度
   * @param height 画布高度
   */
  public async load(data: BackgroundContent, width?: number, height?: number): Promise<boolean> {
    this._data = { ...data };
    if (width !== undefined) this._width = width;
    if (height !== undefined) this._height = height;

    this.clearContent();
    return this._render();
  }

  /**
   * 重载：清除现有内容后重新加载
   */
  public async reload(data: BackgroundContent, width?: number, height?: number): Promise<boolean> {
    return this.load(data, width, height);
  }

  /**
   * 调整背景尺寸（画布 resize 时调用）
   */
  public resize(width: number, height: number): void {
    if (this._width === width && this._height === height) return;
    this._width = width;
    this._height = height;

    // 图片可直接缩放 sprite，颜色/渐变需重绘
    if (this._data.type === BACKGROUND_TYPE.IMAGE && this._sprite) {
      this._sprite.width = this._width;
      this._sprite.height = this._height;
    } else {
      this.clearContent();
      this._render();
    }
  }

  /**
   * 销毁背景，释放所有资源
   */
  public destroy() {
    this.clearContent();
    super.destroy({ children: true });
  }

  // ============================================================
  // 内部方法
  // ============================================================

  /** 清除已渲染内容和缓存 */
  private clearContent(): void {
    if (this._graphics) {
      if (!this._graphics.destroyed) {
        this._graphics.destroy({ children: true, texture: false, context: true });
      }
      this._graphics = null;
    }

    if (this._sprite) {
      if (!this._sprite.destroyed) {
        this._sprite.destroy({ children: true, texture: false, textureSource: false });
      }
      this._sprite = null;
    }

    this._unloadTexture();
    this.removeChildren();
  }

  /** 根据类型分发渲染 */
  private async _render(): Promise<boolean> {
    try {
      switch (this._data.type) {
        case BACKGROUND_TYPE.COLOR:
          return this._renderColor(this._data.content as ColorContent);
        case BACKGROUND_TYPE.IMAGE:
          return await this._renderImage(this._data.content as string);
        case BACKGROUND_TYPE.VIDEO:
          return this._renderVideo(this._data.content as string);
        default:
          console.warn(`BackgroundAsset 未知类型: ${(this._data as BackgroundContent).type}`);
          return false;
      }
    } catch (error) {
      console.error('BackgroundAsset 渲染失败:', error);
      return false;
    }
  }

  // ============================================================
  // 颜色背景
  // ============================================================

  /** 纯色 / 渐变 */
  private _renderColor(colorContent?: ColorContent): boolean {
    const g = new Graphics();
    const hw = this._width / 2;
    const hh = this._height / 2;

    if (!colorContent || !colorContent.type || colorContent.type === COLOR_TYPE.PURE) {
      const color = colorContent?.color ?? '#F8FAFC';
      const alpha = colorContent?.alpha ?? 1;
      g.rect(-hw, -hh, this._width, this._height).fill({ color, alpha });
    } else if (colorContent.type === COLOR_TYPE.LINEAR) {
      g.rect(-hw, -hh, this._width, this._height).fill(this._makeLinearGradient(colorContent));
    } else if (colorContent.type === COLOR_TYPE.RADIAL) {
      g.rect(-hw, -hh, this._width, this._height).fill(this._makeRadialGradient(colorContent));
    }

    this._graphics = g;
    this.addChild(g);
    return true;
  }

  /** 构建线性渐变（textureSpace: 'local'，坐标 0-1 归一化） */
  private _makeLinearGradient(c: ColorContent): FillGradient {
    return new FillGradient({
      textureSpace: 'local',
      type: 'linear',
      start: { x: c.startPoint?.x ?? 0, y: c.startPoint?.y ?? 0 },
      end: { x: c.endPoint?.x ?? 1, y: c.endPoint?.y ?? 0 },
      colorStops: this._normalizeColorStops(c)
    });
  }

  /** 构建径向渐变（textureSpace: 'local'，坐标 0-1 归一化） */
  private _makeRadialGradient(c: ColorContent): FillGradient {
    return new FillGradient({
      textureSpace: 'local',
      type: 'radial',
      center: { x: c.startPoint?.x ?? 0.5, y: c.startPoint?.y ?? 0.5 },
      innerRadius: c.startRadius ?? 0,
      outerCenter: { x: c.endPoint?.x ?? 0.5, y: c.endPoint?.y ?? 0.5 },
      outerRadius: c.endRadius ?? 0.5,
      colorStops: this._normalizeColorStops(c)
    });
  }

  /** 标准化色标，未提供时回退默认双色。hex 字符串转为数字以确保 FillGradient 兼容 */
  private _normalizeColorStops(c: ColorContent) {
    return (
      c.colorStops?.length
        ? c.colorStops
        : [
            { offset: 0, color: c.color ?? '#F8FAFC' },
            { offset: 1, color: '#ffffff' }
          ]
    ).map((s) => ({ offset: s.offset, color: this._toColorNumber(s.color) }));
  }

  /** hex 字符串 → 数字，已是数字则原样返回 */
  private _toColorNumber(color: string | number): number {
    if (typeof color === 'number') return color;
    const hex = color.replace(/^#/, '');
    return parseInt(hex, 16);
  }

  // ============================================================
  // 图片背景
  // ============================================================

  private async _renderImage(url: string): Promise<boolean> {
    if (!url) return false;

    let texture: Texture | null = null;
    try {
      texture = await this._loadTexture(url);
      texture.source.scaleMode = 'linear';

      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5);
      sprite.width = this._width;
      sprite.height = this._height;

      this._sprite = sprite;
      this._loadedUrl = url;
      this.addChild(sprite);
      return true;
    } catch (error) {
      if (texture) textureManager.release(url);
      console.error('BackgroundAsset 图片加载失败:', error);
      return false;
    }
  }

  // ============================================================
  // 视频背景（占位）
  // ============================================================

  private _renderVideo(_url: string): boolean {
    const hw = this._width / 2;
    const hh = this._height / 2;
    const g = new Graphics();
    g.rect(-hw, -hh, this._width, this._height).fill({ color: '#000000' });
    this._graphics = g;
    this.addChild(g);
    return true;
  }

  // ============================================================
  // 纹理工具
  // ============================================================

  private async _loadTexture(url: string): Promise<Texture> {
    return textureManager.loadTexture(url, {
      parser: url.startsWith('blob:') ? 'loadTextures' : undefined
    });
  }

  /**
   * 按 URL 引用计数 release：计数归零才销毁 TextureSource 释放显存。
   * 原理同 ImageElement._unloadOldTexture：共享 source 只在最后一个使用者卸载时销毁，
   * 既避免提前销毁导致渲染崩溃，又不让显存随阅读单调上涨。
   */
  private _unloadTexture(): void {
    if (!this._loadedUrl) return;
    textureManager.release(this._loadedUrl);
    this._loadedUrl = null;
  }

  // ============================================================
  // getters
  // ============================================================

  public get data(): BackgroundContent {
    return this._data;
  }

  public get bgWidth(): number {
    return this._width;
  }

  public get bgHeight(): number {
    return this._height;
  }
}
